import {
  FireDepartmentPickerFragment,
  LepcPickerFragment,
} from "generated-graphql/graphql";

/**
 * Coordinates interface for location data
 */
export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Agency location with coordinates
 */
export interface AgencyLocation {
  id: string;
  name: string;
  type: "lepc" | "fireDepartment" | "facility";
  isPrimary: boolean;
  coordinates?: Coordinates;
  city?: string;
  state?: string;
  county?: string;
}

/**
 * Calculate the great-circle distance between two points using the Haversine formula
 * Yes this was written by claude
 * @param lat1 Latitude of first point in degrees
 * @param lon1 Longitude of first point in degrees
 * @param lat2 Latitude of second point in degrees
 * @param lon2 Longitude of second point in degrees
 * @returns Distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Format distance for display
 * @param distance Distance in miles
 * @returns Formatted string like "2.5 mi" or "Distance unavailable"
 */
export function formatDistance(distance?: number): string {
  if (distance === undefined || distance === null || isNaN(distance)) {
    return "Distance unavailable";
  }

  // Use 2 decimal places for distances under 1 mile, 1 decimal place otherwise
  const precision = distance < 1 ? 2 : 1;
  const formatted = distance.toFixed(precision);

  return `${formatted} mi`;
}

/**
 * Calculate distance between facility and agency if both have coordinates
 * @param facilityCoords Facility coordinates
 * @param agencyCoords Agency coordinates
 * @returns Distance in miles or undefined if coordinates missing
 */
export function calculateAgencyDistance(
  facilityCoords?: Coordinates,
  agencyCoords?: Coordinates
): number | undefined {
  if (!facilityCoords || !agencyCoords) {
    return undefined;
  }

  if (
    !facilityCoords.latitude ||
    !facilityCoords.longitude ||
    !agencyCoords.latitude ||
    !agencyCoords.longitude
  ) {
    return undefined;
  }

  return calculateDistance(
    facilityCoords.latitude,
    facilityCoords.longitude,
    agencyCoords.latitude,
    agencyCoords.longitude
  );
}

/**
 * Extract coordinates from LEPC data
 */
export function getLepcCoordinates(
  lepc: LepcPickerFragment
): Coordinates | undefined {
  if (
    lepc.latitude !== null &&
    lepc.longitude !== null &&
    lepc.latitude !== undefined &&
    lepc.longitude !== undefined
  ) {
    return {
      latitude: lepc.latitude,
      longitude: lepc.longitude,
    };
  }
  return undefined;
}

/**
 * Extract coordinates from FireDepartment data
 */
export function getFireDepartmentCoordinates(
  fd: FireDepartmentPickerFragment
): Coordinates | undefined {
  if (
    fd.latitude !== null &&
    fd.longitude !== null &&
    fd.latitude !== undefined &&
    fd.longitude !== undefined
  ) {
    return {
      latitude: fd.latitude,
      longitude: fd.longitude,
    };
  }
  return undefined;
}

/**
 * Convert agency data to AgencyLocation format
 * @param lepcs Array of LEPC fragments
 * @param fireDepartments Array of FireDepartment fragments
 * @param primaryLepcId ID of primary LEPC
 * @param primaryFireDepartmentId ID of primary fire department
 * @param facilityCoords Facility coordinates for distance calculation
 * @returns Array of AgencyLocation objects
 */
export function convertAgenciesToLocations(
  lepcs: LepcPickerFragment[],
  fireDepartments: FireDepartmentPickerFragment[],
  primaryLepcId?: string,
  primaryFireDepartmentId?: string,
  facilityCoords?: Coordinates
): AgencyLocation[] {
  const locations: AgencyLocation[] = [];

  // Convert LEPCs
  lepcs.forEach((lepc) => {
    const coordinates = getLepcCoordinates(lepc);
    locations.push({
      id: lepc.id,
      name: lepc.name || "Unknown LEPC",
      type: "lepc",
      isPrimary: lepc.id === primaryLepcId,
      coordinates,
      city: lepc.city || undefined,
      state: lepc.state || undefined,
      county: lepc.county || undefined,
    });
  });

  // Convert Fire Departments
  fireDepartments.forEach((fd) => {
    const coordinates = getFireDepartmentCoordinates(fd);
    locations.push({
      id: fd.id,
      name: fd.name || "Unknown Fire Department",
      type: "fireDepartment",
      isPrimary: fd.id === primaryFireDepartmentId,
      coordinates,
      city: fd.city || undefined,
      state: fd.state || undefined,
      county: fd.county || undefined,
    });
  });

  return locations;
}

/**
 * Calculate map bounds that contain all locations
 * @param locations Array of locations with coordinates
 * @returns Bounds object with southwest and northeast corners
 */
export function calculateMapBounds(locations: AgencyLocation[]) {
  const validLocations = locations.filter((loc) => loc.coordinates);

  if (validLocations.length === 0) {
    return null;
  }

  let minLat = validLocations[0].coordinates!.latitude;
  let maxLat = validLocations[0].coordinates!.latitude;
  let minLng = validLocations[0].coordinates!.longitude;
  let maxLng = validLocations[0].coordinates!.longitude;

  validLocations.forEach((location) => {
    const { latitude, longitude } = location.coordinates!;
    minLat = Math.min(minLat, latitude);
    maxLat = Math.max(maxLat, latitude);
    minLng = Math.min(minLng, longitude);
    maxLng = Math.max(maxLng, longitude);
  });

  // Add padding around the bounds
  const padding = 0.01; // Roughly 1km

  return {
    southwest: { lat: minLat - padding, lng: minLng - padding },
    northeast: { lat: maxLat + padding, lng: maxLng + padding },
  };
}

/**
 * Group agencies by type for organized display
 */
export function groupAgenciesByType(locations: AgencyLocation[]) {
  const lepcs = locations.filter((loc) => loc.type === "lepc");
  const fireDepartments = locations.filter(
    (loc) => loc.type === "fireDepartment"
  );

  return {
    lepcs: {
      primary: lepcs.find((l) => l.isPrimary),
      supplemental: lepcs.filter((l) => !l.isPrimary),
    },
    fireDepartments: {
      primary: fireDepartments.find((fd) => fd.isPrimary),
      supplemental: fireDepartments.filter((fd) => !fd.isPrimary),
    },
  };
}


/**
 * Calculate optimal zoom level based on distance
 */
export function calculateOptimalZoom(distance?: number): number {
  if (!distance) return 12;

  if (distance <= 1) return 14; // Very close
  if (distance <= 5) return 12; // Close
  if (distance <= 20) return 10; // Moderate
  if (distance <= 50) return 8; // Far
  return 6; // Very far
}

/**
 * Get agency coordinates based on agency type
 */
export function getAgencyCoordinates(
  agency: LepcPickerFragment | FireDepartmentPickerFragment
): Coordinates | undefined {
  // Check if this is a LEPC or FireDepartment based on available properties
  if ("contactName" in agency) {
    // This is a LEPC
    return getLepcCoordinates(agency as LepcPickerFragment);
  } else {
    // This is a FireDepartment
    return getFireDepartmentCoordinates(agency as FireDepartmentPickerFragment);
  }
}

